import {
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  writeBatch,
  arrayUnion,
  arrayRemove,
  FieldValue,
  getDoc,
} from 'firebase/firestore';
import { db as firestoreInstance } from '@/utils/firebase';
import { 
  businessSchema, 
  Business as BusinessDataFromZod, 
  Member as BusinessMemberData, 
  memberSchema,
  goCampaignSchema,
  baseLeadDeliveryDataSchema,
  promotionSchema,
} from '@/schemas/business';
import { User as UserDataFromZod } from '@/schemas/user';
import { useFirebase } from '@/context/FirebaseProvider';
import { MemberPermission, MemberRole, MemberStatus } from '@/types/schema'; 
import { z } from 'zod';
import { useState } from 'react';

// Define a type for the business member data when adding/updating, before server timestamps
type BusinessMemberPreValidation = Omit<BusinessMemberData, 'invitedAt' | 'joinedAt' | 'addedAt'>;

// Infer GoCampaignObject type from the schema and define SectionKey
export type GoCampaignObject = z.infer<typeof goCampaignSchema>;
export type GoCampaignSectionKey = keyof GoCampaignObject;

// Define default GoCampaign structure
const defaultGoCampaignData: GoCampaignObject = {
  services: { offered: [], targeted: [] },
  locations: { 
    googleCriteriaIdLocations: [], 
    targetedNamedLocations: [],
    countryNames: [], 
    stateNames: [], 
    blockedZipCodes: [] 
  },
  differentiators: { yearsOfExperience: null, diffIds: [] },
  leadDelivery: baseLeadDeliveryDataSchema.omit({ hasBeenSaved: true }).parse({}),
  promotions: [],
  promotionsShowAll: false,
};

export const useBusinessOperations = (
  currentUser: UserDataFromZod | null | undefined,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businesses: businessesFromFirebase } = useFirebase();

  // Function to update business services data specifically
  const updateBusinessServices = async (businessId: string, servicesData: any) => {
    try {
      return await updateBusiness(businessId, { services: servicesData });
    } catch (error) {
      // Error handling without logging
      throw error;
    }
  };
  
  const updateBusiness = async (businessId: string, data: Partial<Omit<BusinessDataFromZod, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      // Ensure 'id' is not part of the data to be updated, as it's immutable.
      const validatedData = businessSchema.partial().omit({ id: true, createdAt: true, updatedAt: true }).parse(data);
      const businessRef = doc(firestoreInstance, 'businesses', businessId);
      await updateDoc(businessRef, {
        ...validatedData,
        updatedAt: serverTimestamp(),
      });
      // Business updated successfully
    } catch (error) {
      // Error handling without logging
      if (error instanceof z.ZodError) {
        // Handle validation errors
      }
      throw error;
    }
  };
  
  const createBusiness = async (
    businessCoreData: Omit<BusinessDataFromZod, 'id' | 'createdAt' | 'updatedAt' | 'members' | 'portfolioId'>, 
    portfolioId: string, 
    ownerId: string
  ): Promise<string> => {
    const newBusinessId = doc(collection(firestoreInstance, 'businesses')).id;

    const ownerMemberDataForValidation: BusinessMemberPreValidation = {
      userId: ownerId,
      email: currentUser?.email || null,
      firstName: currentUser?.firstName || null,
      lastName: currentUser?.lastName || null,
      role: 'Primary Owner', 
      status: 'active',
      permissions: [],
      avatarUrl: currentUser?.avatarUrl || null,
      avatarInitials: currentUser?.avatarInitials || null,
    };
    
    const validatedOwnerMemberInput = memberSchema.omit({ invitedAt: true, joinedAt: true, addedAt: true }).parse(ownerMemberDataForValidation);

    const finalDocumentData = {
      ...businessCoreData,
      id: newBusinessId, 
      portfolioId: portfolioId, 
      members: [{
        ...validatedOwnerMemberInput,
        addedAt: new Date(), 
        invitedAt: new Date(), 
        joinedAt: new Date(),   
      }],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const batch = writeBatch(firestoreInstance);
      const businessRef = doc(firestoreInstance, 'businesses', newBusinessId);
      batch.set(businessRef, finalDocumentData);

      // Link business to user using the ownerId parameter directly
      const userRef = doc(firestoreInstance, 'users', ownerId);
      batch.update(userRef, {
        businesses: arrayUnion(newBusinessId),
        updatedAt: serverTimestamp(),
      });
      
      // Link business to portfolio
      const portfolioRefDoc = doc(firestoreInstance, 'portfolios', portfolioId);
      batch.update(portfolioRefDoc, {
        businessRefs: [newBusinessId], // Explicitly set as an array with the new business ID
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      return newBusinessId;
    } catch (error) {
      // Error handling without logging
      if (error instanceof z.ZodError) {
        // Handle validation errors
      }
      throw error;
    }
  };
  
  const deleteBusiness = async (businessId: string, portfolioId: string, ownerId?: string) => {
    try {
      const batch = writeBatch(firestoreInstance);
      const businessRef = doc(firestoreInstance, 'businesses', businessId);
      batch.delete(businessRef);

      // Remove businessId from user's businesses array
      if (ownerId) {
        const userRef = doc(firestoreInstance, 'users', ownerId);
        batch.update(userRef, {
          businesses: arrayRemove(businessId),
          updatedAt: serverTimestamp(),
        });
      } 
      // Fall back to currentUser if ownerId is not provided
      else if (currentUser && currentUser.id && currentUser.businesses?.includes(businessId)) {
        const userRef = doc(firestoreInstance, 'users', currentUser.id);
        batch.update(userRef, {
          businesses: arrayRemove(businessId),
          updatedAt: serverTimestamp(),
        });
      }
      
      // Remove businessId from portfolio's businessRefs array
      const portfolioRefDoc = doc(firestoreInstance, 'portfolios', portfolioId);
      batch.update(portfolioRefDoc, {
        businessRefs: [], // Set to empty array when removing the business
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
    } catch (error) {
      // Error handling without logging
      throw error;
    }
  };
  
  // Placeholder functions for future implementation
  const addUserToBusiness = (userId: string, businessId: string, role: MemberRole, permissions: MemberPermission[]) => {
    // To be implemented
  };
  
  const removeUserFromBusiness = (userId: string, businessId: string) => {
    // To be implemented
  };

  const saveGoCampaignSection = async <K extends GoCampaignSectionKey>(
    businessId: string,
    sectionKey: K,
    sectionData: GoCampaignObject[K]
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const businessRef = doc(firestoreInstance, 'businesses', businessId);
      const businessSnap = await getDoc(businessRef);

      if (!businessSnap.exists()) {
        throw new Error(`Business with ID ${businessId} not found.`);
      }

      const currentBusinessData = businessSnap.data() as BusinessDataFromZod;
      const existingGoCampaignData: Partial<GoCampaignObject> = currentBusinessData.gocampaign || {};
      
      // Get the default structure for the specific section being saved, if it exists
      const defaultSectionStructure = defaultGoCampaignData[sectionKey];

      // Get the current data for the specific section being saved, if it exists
      const currentSectionState = existingGoCampaignData[sectionKey];

      let newSectionValue;

      // Check if the section's default structure is a plain object (not an array or null)
      // AND the incoming sectionData is also a plain object (not an array or null).
      const isDefaultPlainObject = defaultSectionStructure !== null && typeof defaultSectionStructure === 'object' && !Array.isArray(defaultSectionStructure);
      const isSectionDataPlainObject = sectionData !== null && typeof sectionData === 'object' && !Array.isArray(sectionData);

      if (isDefaultPlainObject && isSectionDataPlainObject) {
        // If both default and new data are plain objects, merge them along with the current state of that section.
        const currentSectionObjectState = (currentSectionState !== null && typeof currentSectionState === 'object' && !Array.isArray(currentSectionState))
          ? (currentSectionState as object)
          : {};

        newSectionValue = {
          ...(defaultSectionStructure as object),
          ...currentSectionObjectState,
          ...(sectionData as object),
        };
      } else {
        // For arrays (like promotions), primitives, or if either default or new data isn't a plain object,
        // the new sectionData simply overwrites.
        newSectionValue = sectionData;
      }

      // Sanitize promotion objects to convert undefined to null before saving
      if (sectionKey === 'promotions' && Array.isArray(newSectionValue)) {
        newSectionValue = newSectionValue.map(promo => {
          const sanitizedPromo: { [key: string]: any } = {};
          for (const key in promo) {
            if (Object.prototype.hasOwnProperty.call(promo, key)) {
              const value = (promo as { [key: string]: any })[key];
              sanitizedPromo[key] = value === undefined ? null : value;
            }
          }
          return sanitizedPromo;
        });
      }

      const updatedGoCampaignData: Partial<GoCampaignObject> = {
        ...existingGoCampaignData, // Preserve all other existing sections
        [sectionKey]: newSectionValue, // Update only the current section with its new, potentially merged, value
      };

      await updateBusiness(businessId, { gocampaign: updatedGoCampaignData as GoCampaignObject });
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Unknown error saving ${sectionKey}`;
      setError(errorMessage);
      if (error instanceof z.ZodError) {
        // Handle validation errors
      }
      throw error; // Re-throw to be caught by the caller
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    updateBusiness,
    createBusiness,
    deleteBusiness,
    addUserToBusiness,
    removeUserFromBusiness,
    updateBusinessServices,
    saveGoCampaignSection,
    isLoading,
    error
  };
};
